import React, { useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Button } from './ui/button';
import { useTranslation } from 'react-i18next';
import LanguageSwitcher from './LanguageSwitcher';
import { BRAND_LOGO_URL } from '../data/brand';

const Navbar = ({ user, logout }) => {
  const { t } = useTranslation();
  const location = useLocation();
  const [isMenuOpen, setIsMenuOpen] = useState(false);

  const isActive = (path) => location.pathname === path;

  const navItems = [
    { path: '/', label: t('nav.dashboard'), icon: '🏠', testid: 'dashboard' },
    { path: '/journal', label: t('nav.journal'), icon: '📝', testid: 'journal' },
    { path: '/activities', label: t('nav.activities'), icon: '🧘‍♀️', testid: 'activities' },
  ];

  if (user?.role === 'admin') {
    navItems.push({ path: '/admin', label: t('nav.admin'), icon: '🛡️', testid: 'admin' });
  }

  return (
    <nav className="fixed top-0 left-0 right-0 z-50 bg-white/80 backdrop-blur-md border-b border-gray-200/50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex justify-between items-center h-16">
          <Link to="/" className="flex items-center space-x-3" data-testid="brand-link">
            <div className="w-11 h-11 rounded-xl overflow-hidden bg-white shadow-lg ring-2 ring-purple-200 bounce-hover">
              <img src={BRAND_LOGO_URL} alt="The Brain Superheroes" className="w-full h-full object-cover" />
            </div>
            <div className="hidden sm:block">
              <span className="font-extrabold text-gray-900 text-lg tracking-tight">{t('nav.appName')}</span>
              <div className="text-xs text-purple-600 font-medium -mt-1">{t('nav.tagline')}</div>
            </div>
          </Link>

          <div className="hidden md:flex items-center space-x-1">
            {navItems.map((item) => (
              <Link
                key={item.path}
                to={item.path}
                className={`px-4 py-2 rounded-lg font-medium transition-all duration-200 ${
                  isActive(item.path) ? 'bg-blue-100 text-blue-700' : 'text-gray-600 hover:text-blue-600 hover:bg-blue-50'
                }`}
                data-testid={`nav-${item.testid}`}
              >
                <span className="mr-2">{item.icon}</span>
                {item.label}
              </Link>
            ))}
          </div>

          <div className="flex items-center space-x-2">
            <LanguageSwitcher />
            <Button onClick={logout} variant="outline" className="btn-secondary" data-testid="logout-btn">
              {t('common.logout')}
            </Button>
            <button
              onClick={() => setIsMenuOpen(!isMenuOpen)}
              className="md:hidden p-2 rounded-lg text-gray-600 hover:text-blue-600 hover:bg-blue-50"
              data-testid="mobile-menu-btn"
            >
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
              </svg>
            </button>
          </div>
        </div>
      </div>

      <div className={`md:hidden transition-all duration-300 ${isMenuOpen ? 'max-h-64 opacity-100' : 'max-h-0 opacity-0 overflow-hidden'}`}>
        <div className="px-4 py-2 bg-white/90 backdrop-blur-md border-t border-gray-200/50">
          {navItems.map((item) => (
            <Link
              key={item.path}
              to={item.path}
              onClick={() => setIsMenuOpen(false)}
              className={`block px-4 py-3 rounded-lg font-medium transition-all duration-200 ${
                isActive(item.path) ? 'bg-blue-100 text-blue-700' : 'text-gray-600 hover:text-blue-600 hover:bg-blue-50'
              }`}
              data-testid={`mobile-nav-${item.testid}`}
            >
              <span className="mr-3">{item.icon}</span>
              {item.label}
            </Link>
          ))}
        </div>
      </div>
    </nav>
  );
};

export default Navbar;
